import styled from '@emotion/styled'
import CheckIcon from '@mui/icons-material/Check'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  Checkbox,
  CircularProgress,
  ClickAwayListener,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import {
  orgsCreateForAuthenticatedUser,
  twinsDeletePicture,
  twinsUpdate,
  twinsUpdateInsightCategories,
  twinsUploadPicture,
  twinsValidateShortUrl,
  usersGetAuthenticated,
  usersUpdateAuthenticated,
  usersUpdateTwinResponder,
} from 'api/endpoints/jelledFromFileSpecWithTransformer'
import {
  Feature,
  OnboardingStep,
  TwinInsightCategory,
  TwinResponderStatus,
  TwinUpdate,
  UserObjective,
  UserRole,
} from 'api/model'
import { COLORS, STRIPE } from 'app-constants'
import EditablePicture from 'components/EditablePicture'
import { OnboardingGoogleLoginButton } from 'components/Login'
import { ReactComponent as GmailIcon } from 'components/Permissions/icons/gmail-icon.svg'
import { ReactComponent as GoogleDriveIcon } from 'components/Permissions/icons/google-drive-icon.svg'
import { CheckboxWithDarkMode, TextFieldWithDarkMode } from 'dark-mode-components'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { useActiveOrganization, useFetchActiveOrganization } from 'hooks/use-organization'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import {
  FtueFailures,
  authTokenAtom,
  authenticatedUserAtom,
  ftueFailStateAtom,
  organizationFamily,
  toastAtom,
} from 'state'
import { myTwinAtom } from 'state/my-twin'
import { featurePermissionsAtomFamily } from 'state/permission'
import { isFieldEmpty } from 'utils'
import { ReactComponent as EmailsGraphic } from '../../assets/onboarding-emails-graphic.svg'
import { ReactComponent as InsightsGraphic } from '../../assets/onboarding-insights-graphic.svg'
import { ReactComponent as IntroGraphic } from '../../assets/onboarding-intro-graphic.svg'
import { ReactComponent as JelledbotsGraphic } from '../../assets/onboarding-jelledbots-graphic.svg'
import { ReactComponent as AppIconDark } from '../NavBar/app-icon-dark.svg'
import { ReactComponent as AppIconLight } from '../NavBar/app-icon-light.svg'
import { ReactComponent as JelledTextDark } from '../NavBar/jelled-text-dark.svg'
import { ReactComponent as JelledTextLight } from '../NavBar/jelled-text-light.svg'
import { OrganizationKind } from './OrganizationKindRadioGroup'
import TwinOverviewModal from './TwinOverviewModal'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import TopicLabelPicker from 'components/Insights/TopicLabelPicker/TopicLabelPicker'
import { useFetchSuggestedCategories } from 'hooks/use-insights'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import { trackButtonClick } from 'analytics/events'

import { useFetchMyTwin, useFetchTwins } from 'hooks/use-twin'
import { FUNCTIONS, FunctionsList, UserRoleList } from './UserInfoModal'

const StageContainerStack = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'isMobile' && prop !== 'isIntroStage',
})<{ isMobile: boolean; isIntroStage: boolean }>`
  align-items: center;
  justify-content: space-between;
  background-color: #ffffff;
  padding-left: ${(props) => (props.isMobile ? '12px' : '64px')};
  padding-top: ${(props) => (props.isMobile ? '12px' : '48px')};
  padding-right: ${(props) => (props.isMobile ? '12px' : '64px')};
  padding-bottom: ${(props) => (props.isMobile ? '12px' : '48px')};
  width: ${(props) => (props.isMobile || props.isIntroStage ? '100%' : '800px')};
  max-width: ${(props) => (props.isIntroStage ? '100%' : '800px')};
  border-radius: ${(props) => (props.isIntroStage || props.isMobile ? '0' : '20px')};
  box-shadow: ${(props) => (props.isIntroStage || props.isMobile ? 'none' : '0px 4px 20px 0px rgba(0, 0, 0, 0.12)')};
  text-align: ${(props) => (props.isIntroStage || props.isMobile ? 'left' : 'center')};
`

enum OnboardingStages {
  'intro' = 'intro',
  'user_role' = 'user_role',
  'function' = 'function',
  'main_objective' = 'main_objective',
  'topics_of_interest' = 'topics_of_interest',
  'create_twin_profile' = 'create_twin_profile',
  'connect_integrations' = 'connect_integrations',
  'subscribe' = 'subscribe',
}

const usernameRegex = /^[a-zA-Z0-9-]+$/

const Header = () => {
  const isMobile = useCheckMobileScreen()

  return (
    <Stack direction="row" spacing="4px" alignItems="center" my={isMobile ? 2 : 6}>
      <AppIconDark />
      <JelledTextDark
        style={{
          marginLeft: '6px',
          width: '84px',
          height: '36px',
        }}
      />
    </Stack>
  )
}

const IntroStage = ({
  page,
  setPage,
  setStage,
  tosChecked,
  setTosChecked,
}: {
  page: number
  setPage: React.Dispatch<React.SetStateAction<number>>
  setStage: (stage: OnboardingStages) => void
  tosChecked: boolean
  setTosChecked: (tosChecked: boolean) => void
}) => {
  const isMobile = useCheckMobileScreen()
  const [, setActiveOrganization] = useActiveOrganization()

  const [, setSearchParams] = useSearchParams()

  const getTitleText = () => {
    switch (page) {
      case 0:
        return `Hi, ${user?.name}!`
      case 1:
        return 'Stay hyperaware through insights.'
      case 2:
        return 'Optimize your email routines with automated email drafting.'
      case 3:
        return 'Streamline your conversations with digital twins.'
    }
    return ''
  }

  const getContent = () => {
    switch (page) {
      case 0:
        return [
          'Welcome to Jelled.ai, where you can create and use your digital twin to streamline your productivity.',
          'Before we get started, let’s take a quick tour.',
        ]
      case 1:
        return [
          'After you connect your Gmail, Google Drive, your digital twin can process your email content and surface insights in the form of topics and trends that you can easily track.',
        ]
      case 2:
        return [
          'Your digital twin can generate timely and informed email drafts that incorporate its full knowledge base and the thread context.',
          'Over time, your digital twin learns your email response patterns and saves you time and energy by drafting responses to the most important messages.',
        ]
      case 3:
        return [
          'Converse with digital twins of your coworkers to receive instant responses.',
          'Add data to your digital twin’s knowledge base to create your own contextually-aware twin that can respond to incoming queries. You can also monitor and edit your twin’s messages.',
        ]
    }
    return []
  }

  const renderGraphic = () => {
    if (!isMobile) {
      return null
    }

    switch (page) {
      case 0:
        return (
          <IntroGraphic
            style={{
              borderRadius: '8px',
              width: 'calc(100vw - 32px)',
              height: 'calc(100vw - 32px)',
              marginBottom: '82px',
            }}
          />
        )
      case 1:
        return (
          <InsightsGraphic
            style={{
              borderRadius: '8px',
              width: 'calc(100vw - 32px)',
              height: '100%',
              marginBottom: '82px',
            }}
          />
        )
      case 2:
        return (
          <EmailsGraphic
            style={{
              borderRadius: '8px',
              width: 'calc(100vw - 32px)',
              height: '100%',
              marginBottom: '82px',
            }}
          />
        )
      case 3:
        return (
          <JelledbotsGraphic
            style={{
              borderRadius: '8px',
              width: 'calc(100vw - 32px)',
              height: '100%',
              marginBottom: '82px',
            }}
          />
        )
    }
  }

  const onTosChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTosChecked(event.target.checked)
    setIsSubmitting(false)
    setSubmitCount(0)
  }

  const [user, setUser] = useRecoilState(authenticatedUserAtom)
  const [, setAuthToken] = useRecoilState(authTokenAtom)
  const [submitCount, setSubmitCount] = useState<number>(0)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const setOrganization = useSetRecoilState(organizationFamily(user?.organization_id ?? ''))

  const onFinishClick = async () => {
    setSubmitCount((submitCount) => submitCount + 1)
    setIsSubmitting(true)

    if (!tosChecked) {
      // do not allow user to proceed
      return
    }

    const response = await usersUpdateAuthenticated({
      timezone_offset: -new Date().getTimezoneOffset(),
      tos_signed: true,
    })
    setUser(response.data)

    const hasLinkedOrganization = user?.profiles?.length !== 0
    if (!hasLinkedOrganization) {
      const organizationNew = {
        organization_kind: OrganizationKind.company,
      }
      const orgCreateResponse = await orgsCreateForAuthenticatedUser(organizationNew)
      setOrganization(orgCreateResponse.data)
      // need to set this as active org as well
      setActiveOrganization(orgCreateResponse.data.id)

      setUser((user) => {
        if (!user) {
          return null
        }

        return {
          ...user,
          organization_id: orgCreateResponse.data.id,
        }
      })
    }

    // should refetch user to get the updated auth token
    const userResponse = await usersGetAuthenticated()
    if (userResponse.data.token) {
      setAuthToken(userResponse.data.token)
    }

    // should update the url as that's the new stage that we're on
    setSearchParams(`?stage=${OnboardingStages.user_role}`)
    setStage(OnboardingStages.user_role)
    trackButtonClick('[Onboarding 1] intro stage')
  }

  return (
    <StageContainerStack
      isIntroStage={true}
      isMobile={isMobile}
      justifyContent="space-between"
      sx={{
        height: '100%',
      }}>
      <Stack width="100%">
        {!isMobile && <Header />}
        <Typography variant={isMobile ? 'h3' : 'h1'} color="secondary" mb={isMobile ? '12px' : '36px'}>
          {getTitleText()}
        </Typography>

        {getContent().map((content, index) => (
          <Typography key={index} variant="pS" mb={2} color="secondary">
            {content}
          </Typography>
        ))}

        {renderGraphic()}
      </Stack>

      <Stack spacing={2} width="100%">
        <Button
          disabled={isSubmitting}
          variant="contained"
          color="primary"
          onClick={() => {
            // if page is less than 3, increment the page
            if (page < 3) {
              setPage((page) => page + 1)
            } else {
              onFinishClick()
            }
          }}
          sx={{
            alignSelf: 'center',
            bottom: isMobile ? '12px' : 'inherit',
            position: isMobile ? 'fixed' : 'relative',
            width: isMobile ? 'calc(100vw - 36px)' : '100%',
            marginBottom: isMobile ? '12px' : '0',
          }}>
          {page === 3 ? "Alright, let's get started" : 'Continue'}
        </Button>

        {page === 3 && (
          <FormControl
            error={!tosChecked}
            style={{
              width: isMobile ? '92vw' : 'auto',
            }}>
            <FormControlLabel
              control={<Checkbox checked={tosChecked} onChange={onTosChange} color="primary" />}
              label={
                <label
                  style={{
                    color: COLORS.GRAY_0,
                    fontSize: isMobile ? '12px' : '14px',
                  }}>
                  I agree to the{' '}
                  <a
                    href="https://www.jelled.ai/terms-of-service"
                    style={{ color: COLORS.JELLED_PRIMARY_BLUE, textDecoration: 'none' }}
                    target="_blank"
                    rel="noreferrer">
                    Terms of service
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://www.jelled.ai/privacy-policy"
                    style={{ color: COLORS.JELLED_PRIMARY_BLUE, textDecoration: 'none' }}
                    target="_blank"
                    rel="noreferrer">
                    Privacy policy
                  </a>{' '}
                </label>
              }
              componentsProps={{ typography: { variant: 'pS' } }}
            />
            {!tosChecked && submitCount > 0 && (
              <FormHelperText style={{ marginTop: '-8px' }}>You must agree in order to proceed</FormHelperText>
            )}
          </FormControl>
        )}
      </Stack>
    </StageContainerStack>
  )
}

// used to reference the possible roles that a user could have (user.role)

const UserRoleStage = ({ setStage }: { setStage: (stage: OnboardingStages) => void }) => {
  const isMobile = useCheckMobileScreen()

  const [, setSearchParams] = useSearchParams()
  const setFtueFail = useSetRecoilState(ftueFailStateAtom)

  const [user, setUser] = useRecoilState(authenticatedUserAtom)

  const [submitCount, setSubmitCount] = useState<number>(0)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [selectedRole, setSelectedRole] = useState<UserRole | null>(user?.role ?? null)

  const onNextClick = async () => {
    setSubmitCount((submitCount) => submitCount + 1)
    setIsSubmitting(true)

    if (!selectedRole) {
      setIsSubmitting(false)
      return
    }

    try {
      const response = await usersUpdateAuthenticated({
        role: selectedRole,
      })
      setSearchParams(`?stage=${OnboardingStages.function}`)
      setStage(OnboardingStages.function)

      setUser(response.data)
      // [Analytics] Track user creation of account
      trackButtonClick('[Onboarding 3] User account created')
    } catch (error) {
      // TODO:
      //   1. Log an error here when we have a logging setup
      //   2. Possibly do something else when internet connection is down
      setFtueFail(FtueFailures.AccountCreationFailure)
    } finally {
      setIsSubmitting(false)
    }
  }

  // if the user has a linked organization, we should disable the back button for viewing the
  // organization / individual selection stage
  const hasLinkedOrganization = user?.profiles?.length !== 0

  return (
    <StageContainerStack
      isMobile={isMobile}
      isIntroStage={false}
      sx={{
        height: isMobile ? '100%' : '600px',
      }}>
      <Stack width="100%">
        <Typography color="secondary" variant="h3" mb="14px">
          How would you describe your role?
        </Typography>

        <Typography
          color="secondary"
          variant="pS"
          mb={3}
          sx={{
            color: COLORS.GRAY_2,
          }}>
          This will help us customize your experience for your needs.
        </Typography>

        <UserRoleList selectedRole={selectedRole} setSelectedRole={setSelectedRole} />

        {submitCount > 0 && !selectedRole && (
          <Typography
            variant="pXS"
            sx={{
              color: COLORS.RED,
              mt: 5,
              width: '608px',
              textAlign: 'left',
              alignSelf: 'center',
            }}>
            * you must select an option to proceed
          </Typography>
        )}
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        justifyContent="flex-end"
        width="100%"
        mr={0}
        position={isMobile ? 'fixed' : 'relative'}
        bottom={isMobile ? '24px' : 'auto'}
        right={isMobile ? '24px' : 'auto'}>
        {!hasLinkedOrganization && (
          <Button
            size="small"
            variant="text"
            sx={{
              width: '84px',
            }}
            onClick={() => {
              setSearchParams(`?stage=${OnboardingStages.intro}`)
              setStage(OnboardingStages.intro)
            }}>
            Back
          </Button>
        )}
        <LoadingButton
          disabled={!selectedRole}
          loading={isSubmitting}
          size="small"
          variant="contained"
          sx={{
            width: '84px',
          }}
          onClick={onNextClick}>
          Next
        </LoadingButton>
      </Stack>
    </StageContainerStack>
  )
}

const FunctionStage = ({ setStage }: { setStage: (stage: OnboardingStages) => void }) => {
  const isMobile = useCheckMobileScreen()

  const [, setSearchParams] = useSearchParams()
  const setFtueFail = useSetRecoilState(ftueFailStateAtom)

  const [user, setUser] = useRecoilState(authenticatedUserAtom)

  const [submitCount, setSubmitCount] = useState<number>(0)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [selectedFunction, setSelectedFunction] = useState<string>(user?.function ?? '')

  // if the user specifies a custom function, we want to set that instead of 'Other'
  const [customFunction, setCustomFunction] = useState<string>('')
  // when the user is loaded, we want to check to see if the user has a custom function
  useEffect(() => {
    if (!user) {
      return
    }

    const isCustomFunction = !FUNCTIONS.find((f) => f.text === user.function)

    if (isCustomFunction && user.function) {
      setSelectedFunction('other')
      setCustomFunction(user.function)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onCustomFunctionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCustomFunction(event.target.value)
  }

  const onNextClick = async () => {
    setSubmitCount((submitCount) => submitCount + 1)
    setIsSubmitting(true)

    if (!selectedFunction) {
      setIsSubmitting(false)
      return
    }

    try {
      const response = await usersUpdateAuthenticated({
        function: customFunction ? customFunction : selectedFunction,
      })
      setUser(response.data)

      setStage(OnboardingStages.main_objective)
      setSearchParams(`?stage=${OnboardingStages.main_objective}`)

      // [Analytics] Track user creation of account
      trackButtonClick('[Onboarding 3] User account created')
    } catch (error) {
      // TODO:
      //   1. Log an error here when we have a logging setup
      //   2. Possibly do something else when internet connection is down
      setFtueFail(FtueFailures.AccountCreationFailure)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <StageContainerStack
      isMobile={isMobile}
      isIntroStage={false}
      sx={{
        height: isMobile ? '100%' : '600px',
      }}>
      <Stack width="100%">
        <Typography color="secondary" variant="h3" mb="14px">
          What kind of work do you do?
        </Typography>

        <Typography
          color="secondary"
          variant="pS"
          mb={3}
          sx={{
            color: COLORS.GRAY_2,
          }}>
          We'll use this to suggest some useful insight topics to help you get started.
        </Typography>

        <FunctionsList
          selectedFunction={selectedFunction}
          setSelectedFunction={setSelectedFunction}
          setCustomFunction={setCustomFunction}
        />

        {submitCount > 0 && !selectedFunction && (
          <Typography
            variant="pXS"
            sx={{
              color: COLORS.RED,
              mt: 5,
              width: '608px',
              textAlign: 'left',
              alignSelf: 'center',
            }}>
            * you must select an option to proceed
          </Typography>
        )}

        {selectedFunction === 'other' && (
          <TextField
            className="has-label"
            label="Other"
            placeholder="e.g. Design"
            value={customFunction}
            onChange={onCustomFunctionChange}
            sx={{
              width: isMobile ? '100%' : '400px',
              marginTop: '32px',
              alignSelf: 'left',
              marginLeft: '32px',
              marginBottom: '32px',
            }}
          />
        )}
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        justifyContent="space-between"
        width="100%"
        mr={0}
        position={isMobile ? 'fixed' : 'relative'}
        bottom={isMobile ? '24px' : 'auto'}
        right={isMobile ? '24px' : 'auto'}>
        <Button
          size="small"
          variant="text"
          onClick={() => {
            setSearchParams(`?stage=${OnboardingStages.user_role}`)
            setStage(OnboardingStages.user_role)
          }}>
          Back
        </Button>
        <LoadingButton
          disabled={selectedFunction === ''}
          loading={isSubmitting}
          size="small"
          variant="contained"
          sx={{
            width: '84px',
          }}
          onClick={onNextClick}>
          Next
        </LoadingButton>
      </Stack>
    </StageContainerStack>
  )
}

const MainObjectiveCard = ({
  title,
  points,
  onCheck,
  isChecked,
}: {
  title: string
  points: string[]
  onCheck: (isChecked: boolean) => void
  isChecked: boolean
}) => {
  const isMobile = useCheckMobileScreen()

  return (
    <Stack
      alignSelf="center"
      borderRadius="8px"
      direction="row"
      spacing="10px"
      width={isMobile ? 'calc(100%)' : '389px'}
      alignItems="center"
      justifyContent="space-between"
      onClick={() => onCheck(!isChecked)}
      p={2}
      pl={4}
      sx={{
        border: isChecked ? `1px solid ${COLORS.JELLED_PRIMARY_BLUE}` : `1px solid ${COLORS.GRAY_5}`,
        background: isChecked ? COLORS.JELLED_MID_BLUE_10_PERCENT : 'transparent',
        cursor: 'pointer',
        '&:hover': {
          background: COLORS.JELLED_MID_BLUE_10_PERCENT,
        },
      }}>
      <Stack>
        <Typography
          variant="h5"
          sx={{
            mb: '10px',
            ml: -2,
            textAlign: 'left',
          }}>
          {title}
        </Typography>

        <ul
          style={{
            padding: 0,
            margin: 0,
          }}>
          {points.map((point, index) => (
            <li key={index} style={{ textAlign: 'left' }}>
              <Typography
                key={index}
                variant={isMobile ? 'pXXS' : 'pS'}
                sx={{
                  margin: 0,
                  textAlign: 'left',
                }}>
                {point}
              </Typography>
            </li>
          ))}
        </ul>
      </Stack>

      {isChecked ? (
        <CheckBoxIcon
          sx={{
            color: COLORS.JELLED_PRIMARY_BLUE,
          }}
        />
      ) : (
        <CheckBoxOutlineBlankIcon
          sx={{
            color: COLORS.GRAY_5,
          }}
        />
      )}
    </Stack>
  )
}

const MainObjectiveStage = ({ setStage }: { setStage: (stage: OnboardingStages) => void }) => {
  const isMobile = useCheckMobileScreen()

  const [, setSearchParams] = useSearchParams()
  const setFtueFail = useSetRecoilState(ftueFailStateAtom)

  const [, setUser] = useRecoilState(authenticatedUserAtom)

  const [submitCount, setSubmitCount] = useState<number>(0)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const [isEmailChecked, setIsEmailChecked] = useState<boolean>(true)
  const [isKnowledgeBaseChecked, setIsKnowledgeBaseChecked] = useState<boolean>(true)

  const onNextClick = async () => {
    setSubmitCount((submitCount) => submitCount + 1)
    setIsSubmitting(true)

    // there needs to be at least one option checked
    if (!isEmailChecked && !isKnowledgeBaseChecked) {
      setIsSubmitting(false)
      return
    }

    try {
      let objectives: UserObjective[] = []
      if (isEmailChecked) {
        objectives.push(UserObjective.email_management)
      }
      if (isKnowledgeBaseChecked) {
        objectives.push(UserObjective.knowledge_base)
      }
      const response = await usersUpdateAuthenticated({
        objectives,
      })
      setUser(response.data)

      // if the user selected at least email, we want to take them to the topics of interest stage
      if (isEmailChecked) {
        setSearchParams(`?stage=${OnboardingStages.topics_of_interest}`)
        setStage(OnboardingStages.topics_of_interest)
      } else {
        // otherwise, we want to take them to creating the twin profile
        setSearchParams(`?stage=${OnboardingStages.create_twin_profile}`)
        setStage(OnboardingStages.create_twin_profile)
      }
    } catch (error) {
      // TODO:
      //   1. Log an error here when we have a logging setup
      //   2. Possibly do something else when internet connection is down
      setFtueFail(FtueFailures.AccountCreationFailure)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <StageContainerStack
      isMobile={isMobile}
      isIntroStage={false}
      sx={{
        height: isMobile ? '100%' : '600px',
      }}>
      <Stack width="100%">
        <Typography color="secondary" variant="h3" mb="14px">
          What's your main objective with Jelled.ai?
        </Typography>

        <Typography
          color="secondary"
          variant="pS"
          mb={3}
          sx={{
            color: COLORS.GRAY_2,
          }}>
          This will help us personalize your in-app experience.
        </Typography>

        <Stack spacing="4px">
          <MainObjectiveCard
            title="Intelligent email management"
            points={[
              'get insights and trends from your emails',
              'automatically generate drafts using AI',
              'sort incoming emails smartly',
            ]}
            onCheck={setIsEmailChecked}
            isChecked={isEmailChecked}
          />

          <MainObjectiveCard
            title="Collaborative knowledge base"
            points={[
              'curate your personal knowledge base',
              'share your knowledge via your digital twin',
              'communicate asynchronously with your team',
            ]}
            onCheck={setIsKnowledgeBaseChecked}
            isChecked={isKnowledgeBaseChecked}
          />
        </Stack>
        {submitCount > 0 && !isEmailChecked && !isKnowledgeBaseChecked && (
          <Typography
            variant="pXS"
            sx={{
              color: COLORS.RED,
              mt: '15px',
              textAlign: 'left',
              alignSelf: 'center',
              width: '389px',
            }}>
            * you must select an option to proceed
          </Typography>
        )}
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        justifyContent="flex-end"
        width="100%"
        mr={0}
        position={isMobile ? 'fixed' : 'relative'}
        bottom={isMobile ? '24px' : 'auto'}
        right={isMobile ? '24px' : 'auto'}>
        <Button
          size="small"
          variant="text"
          sx={{
            width: '84px',
          }}
          onClick={() => {
            setSearchParams(`?stage=${OnboardingStages.function}`)
            setStage(OnboardingStages.function)
          }}>
          Back
        </Button>
        <LoadingButton
          loading={isSubmitting}
          size="small"
          variant="contained"
          sx={{
            width: '84px',
          }}
          onClick={onNextClick}>
          Next
        </LoadingButton>
      </Stack>
    </StageContainerStack>
  )
}

export const TopicColorItem = ({
  topic,
  onUpdateColor,
}: {
  topic: TwinInsightCategory
  onUpdateColor: (topicId: string, color: string) => void
}) => {
  const [isPickerOpen, setIsPickerOpen] = useState<boolean>(false)

  const onSelectColor = (color: string) => {
    // should update the color of the topic
    onUpdateColor(topic.id, color)
    setIsPickerOpen(false)
  }

  return (
    <Stack position="relative">
      <Stack
        onClick={() => setIsPickerOpen(true)}
        // onclick of this should bring up the color picker
        sx={{
          height: '24px',
          width: '24px',
          background: topic.color,
          borderRadius: '4px',
          cursor: 'pointer',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
        <EditOutlinedIcon
          sx={{
            fill: COLORS.PURE_WHITE,
            height: '20px',
            width: '20px',
          }}
        />
      </Stack>

      {isPickerOpen && (
        <ClickAwayListener onClickAway={() => setIsPickerOpen(false)}>
          <Stack
            sx={{
              borderRadius: '8px',
              position: 'absolute',
              zIndex: 1,
              top: '32px',
              width: '164px',
              background: COLORS.PURE_WHITE,
              boxShadow: '2px 2px 12px 0px rgba(0, 0, 0, 0.14)',
            }}>
            <TopicLabelPicker selectedColor={topic.color} onSelectColor={onSelectColor} />
          </Stack>
        </ClickAwayListener>
      )}
    </Stack>
  )
}

const TopicsOfInterestStage = ({
  isApplyingTopicsAsLabels,
  setIsApplyingTopicsAsLabels,
  setStage,
}: {
  setStage: (stage: OnboardingStages) => void
  isApplyingTopicsAsLabels: boolean
  setIsApplyingTopicsAsLabels: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const [activeOrganization] = useActiveOrganization()
  const isMobile = useCheckMobileScreen()
  const theme = useTheme()

  const [user, setUser] = useRecoilState(authenticatedUserAtom)
  const { myTwin } = useFetchMyTwin(user?.id ?? '')

  const [, setSearchParams] = useSearchParams()
  const setFtueFail = useSetRecoilState(ftueFailStateAtom)

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const onNextClick = async () => {
    if (!activeOrganization || !myTwin?.id || !user) {
      return
    }

    setIsSubmitting(true)

    try {
      // first, we update the twin insight categories
      await twinsUpdateInsightCategories(activeOrganization, myTwin.id, suggestedCategories)

      // we need to update the user's onboarding steps completed
      const response = await usersUpdateAuthenticated({
        onboarding_steps_completed: [
          ...user.onboarding_steps_completed,
          OnboardingStep.topics_of_interest_update_finished,
        ],
      })
      setUser(response.data)

      // also need to update the twin responder if isApplyingTopicsAsLabels is true
      if (isApplyingTopicsAsLabels) {
        await usersUpdateTwinResponder(activeOrganization, myTwin.id, {
          use_insight_topics_as_labels: true,
        })
      }

      setSearchParams(`?stage=${OnboardingStages.create_twin_profile}`)
      setStage(OnboardingStages.create_twin_profile)
    } catch (error) {
      // TODO:
      //   1. Log an error here when we have a logging setup
      //   2. Possibly do something else when internet connection is down
      setFtueFail(FtueFailures.AccountCreationFailure)
    } finally {
      setIsSubmitting(false)
    }
  }

  const onCheckApplyingTopicsAsLabels = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsApplyingTopicsAsLabels(event.target.checked)
  }

  const { isLoading, suggestedCategories, setSuggestedCategories } = useFetchSuggestedCategories(myTwin?.id ?? '')

  const onUpdateColor = (topicId: string, color: string) => {
    const updatedTopics = suggestedCategories.map((topic) => {
      if (topic.id === topicId) {
        return {
          ...topic,
          color,
        }
      }

      return topic
    })

    setSuggestedCategories(updatedTopics)
  }

  if (isLoading) {
    return (
      <StageContainerStack
        isMobile={isMobile}
        isIntroStage={false}
        sx={{
          height: isMobile ? '100%' : '600px',
        }}>
        <CircularProgress
          size="24px"
          thickness={4}
          style={{
            marginTop: '30vh',
            color: COLORS.JELLED_PRIMARY_BLUE,
            alignSelf: 'center',
          }}
        />
      </StageContainerStack>
    )
  }

  return (
    <StageContainerStack
      isMobile={isMobile}
      isIntroStage={false}
      sx={{
        height: isMobile ? '100%' : '600px',
      }}>
      <Stack width="100%">
        <Typography color="secondary" variant="h3" mb="14px">
          Configure topics of interest
        </Typography>

        <Typography
          color="secondary"
          variant="pS"
          sx={{
            color: COLORS.GRAY_2,
          }}>
          Here are some suggested topics that your insights from Gmail and Slack will be organized by. You can always
          add more topics later.
        </Typography>

        <Stack spacing="12px" my={5}>
          {suggestedCategories.map((topic, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="center">
              <TopicColorItem topic={topic} onUpdateColor={onUpdateColor} />

              <Typography variant="pXS" textAlign="left">
                {topic.name}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Stack direction="row" alignItems="center">
          <CheckboxWithDarkMode
            disableRipple
            theme={theme}
            checked={isApplyingTopicsAsLabels}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onCheckApplyingTopicsAsLabels(e)}
            color="primary"
            sx={{
              padding: 0,
              height: '42px',
              width: 'inherit',
            }}
          />
          <Typography variant="pS">Apply topics as labels in Gmail to organize incoming emails</Typography>
        </Stack>
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        justifyContent="flex-end"
        width="100%"
        mr={0}
        position={isMobile ? 'fixed' : 'relative'}
        bottom={isMobile ? '24px' : 'auto'}
        right={isMobile ? '24px' : 'auto'}>
        <Button
          size="small"
          variant="text"
          sx={{
            width: '84px',
          }}
          onClick={() => {
            setSearchParams(`?stage=${OnboardingStages.main_objective}`)
            setStage(OnboardingStages.main_objective)
          }}>
          Back
        </Button>
        <LoadingButton
          loading={isSubmitting}
          size="small"
          variant="contained"
          sx={{
            width: '84px',
          }}
          onClick={onNextClick}>
          Next
        </LoadingButton>
      </Stack>
    </StageContainerStack>
  )
}

const ConnectIntegrationsStage = ({
  acceptedPermissions,
  missingPermissions,
}: {
  acceptedPermissions: string[]
  missingPermissions: string[]
}) => {
  const isMobile = useCheckMobileScreen()

  return (
    <StageContainerStack
      isMobile={isMobile}
      isIntroStage={false}
      sx={{
        height: isMobile ? '100%' : '600px',
      }}>
      <Stack width="100%" mb={4}>
        <Typography color="secondary" variant="h3" mb="14px">
          Link your Gmail and Google Drive
        </Typography>

        <Typography
          color="secondary"
          variant="pS"
          mb={3}
          alignSelf="center"
          maxWidth={isMobile ? '100%' : '489px'}
          sx={{
            color: COLORS.GRAY_2,
          }}>
          We use your knowledge from your emails and documents for the sole purpose of providing contextual insights and
          communication.
        </Typography>

        <Stack alignItems="center" mb={7}>
          <Stack direction="row" alignItems="center" spacing="22px" mb={4} mt={4}>
            <GmailIcon
              style={{
                flex: '0 0 40px',
              }}
            />
            <GoogleDriveIcon
              style={{
                flex: '0 0 40px',
              }}
            />
          </Stack>

          <OnboardingGoogleLoginButton
            acceptedPermissions={acceptedPermissions}
            missingPermissions={missingPermissions}
          />
        </Stack>
      </Stack>

      <Typography
        variant="pXS"
        sx={{
          color: COLORS.GRAY_2,
        }}>
        Learn more about how Jelled.ai{' '}
        <Link
          href="https://jelled.ai/security"
          target="_blank"
          sx={{
            textDecoration: 'underline',
            color: COLORS.GRAY_2,
          }}>
          protects your data
        </Link>
      </Typography>
    </StageContainerStack>
  )
}

export function isValidUsername(username: string, isUsernameAvailable: boolean | null) {
  return isUsernameAvailable && usernameRegex.test(username) && (username.length >= 3 || username.length <= 32)
}

export const UsernameInput = ({
  isUsernameAvailable,
  setIsUsernameAvailable,
  onShortUrlChange,
  twinId,
  username,
  didSubmitForm,
  shouldCheckAvailabilityOnMount,
}: {
  isUsernameAvailable: boolean | null
  setIsUsernameAvailable: React.Dispatch<React.SetStateAction<boolean | null>>
  onShortUrlChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  twinId: string
  username: string
  didSubmitForm?: boolean
  shouldCheckAvailabilityOnMount?: boolean
}) => {
  const [activeOrganization] = useActiveOrganization()

  const isUsernameLengthInvalid = username.length < 3 || username.length > 32
  const isUsernameFormatInvalid = !usernameRegex.test(username)
  const isUsernameInvalid = isUsernameAvailable === false || isUsernameLengthInvalid || isUsernameFormatInvalid
  const [submitCount, setSubmitCount] = useState<number>(0)

  const onUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onShortUrlChange(e)
  }

  const checkUsernameAvailability = useCallback(
    async () => {
      if (!activeOrganization) {
        return
      }

      try {
        setSubmitCount((submitCount) => submitCount + 1)
        const response = await twinsValidateShortUrl(activeOrganization, twinId, {
          short_url: username,
        })
        setIsUsernameAvailable(response.data.available as boolean)
      } catch (error) {
        setIsUsernameAvailable(null)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeOrganization, twinId, setIsUsernameAvailable, username]
  )

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (username && shouldCheckAvailabilityOnMount) {
        checkUsernameAvailability()
      }
    }, 1000)

    return () => {
      clearTimeout(timeout)
    }
  }, [username, checkUsernameAvailability, shouldCheckAvailabilityOnMount])

  const getHelperText = () => {
    if (isUsernameLengthInvalid) {
      return 'Username must be between 3 - 32 characters'
    } else if (isUsernameFormatInvalid) {
      return 'Username can only contain numbers, letters, and a hyphen (-)'
    } else if (isUsernameAvailable === false) {
      return 'Username is unavailable. Try adding numbers and uppercase letters.'
    } else {
      return ''
    }
  }

  const theme = useTheme()

  return (
    <Stack>
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <TextFieldWithDarkMode
          isDarkBackdrop={true}
          theme={theme}
          className="has-label"
          label={
            <Stack direction="row" alignItems="center">
              <Typography
                variant="h5"
                sx={{
                  color: COLORS.GRAY_2,
                }}>
                Twin handle
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: COLORS.RED,
                }}>
                *
              </Typography>

              <Tooltip title="This will affect your twin profile link." enterTouchDelay={0}>
                <InfoOutlined
                  sx={{
                    height: '16px',
                    width: '16px',
                    color: COLORS.GRAY_3,
                    ml: 1,
                  }}
                />
              </Tooltip>
            </Stack>
          }
          // add an input adornment with a checkmark or an x to indicate if the username is available
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {submitCount === 0 && null}
                {submitCount > 0 && isUsernameAvailable === true && !isUsernameInvalid && (
                  <CheckIcon
                    sx={{
                      color: COLORS.GREEN,
                    }}
                  />
                )}
                {submitCount > 0 && isUsernameAvailable === false && isUsernameInvalid && (
                  <CloseIcon
                    sx={{
                      color: COLORS.RED,
                    }}
                  />
                )}
              </InputAdornment>
            ),
          }}
          placeholder="Enter a twin handle"
          value={username}
          onChange={onUsernameChange}
          error={(submitCount > 0 || didSubmitForm) && isUsernameInvalid}
          helperText={(submitCount > 0 || didSubmitForm) && getHelperText()}
          sx={{
            width: '100%',
          }}
        />
      </Stack>
    </Stack>
  )
}

const CreateTwinProfileStage = ({
  username,
  onShortUrlChange,
  isUsernameAvailable,
  setIsUsernameAvailable,
  twinName,
  setTwinName,
  publicSummary,
  setTwinPublicSummary,
  title,
  setStage,
  setTitle,
  missingPermissions,
}: {
  username: string
  onShortUrlChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  isUsernameAvailable: boolean | null
  setIsUsernameAvailable: React.Dispatch<React.SetStateAction<boolean | null>>
  picture: string
  setPicture: (picture: string) => void
  twinName: string
  setTwinName: (twinName: string) => void
  publicSummary: string
  setTwinPublicSummary: (publicSummary: string) => void
  title: string
  setStage: (stage: OnboardingStages) => void
  setTitle: (title: string) => void
  missingPermissions: boolean
}) => {
  const [activeOrganization] = useActiveOrganization()
  const isMobile = useCheckMobileScreen()
  const setToast = useSetRecoilState(toastAtom)
  const navigate = useNavigate()

  const { organization } = useFetchActiveOrganization()

  const [user, setUser] = useRecoilState(authenticatedUserAtom)
  const { setTwins } = useFetchTwins()
  const myTwin = useRecoilValue(myTwinAtom)

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [submitCount, setSubmitCount] = useState<number>(0)

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTwinName(e.target.value)
  }

  const onTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value)
  }

  const onPublicSummaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTwinPublicSummary(e.target.value)
  }

  const onPicturePicked: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
    if (!organization || !myTwin || !event.target.files) {
      return
    }

    try {
      const file = event.target.files[0]
      const result = await twinsUploadPicture(organization.id, myTwin.id, file, {
        headers: { 'Content-Type': 'application/octet-stream' },
      })

      // update twin picture in recoil
      setTwins((twins) => {
        if (!twins) {
          return []
        }
        return twins.map((t) => {
          if (t.id === myTwin.id) {
            return Object.assign({}, t, { picture: result.data.picture })
          }
          return t
        })
      })
    } catch (Error) {
      setToast({ title: 'Failed to upload twin photo', severity: 'error' })
    }
  }

  const onPictureRemoved = async () => {
    if (!organization || !myTwin) {
      return
    }

    try {
      await twinsDeletePicture(organization.id, myTwin.id)
      setTwins((twins) => {
        if (!twins) {
          return []
        }
        return twins.map((t) => {
          if (t.id === myTwin.id) {
            return Object.assign({}, t, { picture: null })
          }
          return t
        })
      })
    } catch (error) {
      setToast({ title: 'Twin photo removal failed', severity: 'error' })
    }
  }

  const onNextClick = async () => {
    setIsSubmitting(true)
    setSubmitCount((submitCount) => submitCount + 1)

    if (
      !activeOrganization ||
      !myTwin ||
      isUsernameAvailable === null ||
      isFieldEmpty(twinName) ||
      isFieldEmpty(publicSummary) ||
      isFieldEmpty(title) ||
      (username && !isValidUsername(username, isUsernameAvailable)) ||
      !user
    ) {
      setIsSubmitting(false)
      return
    }

    try {
      // first, we need to update the twin info
      const update: TwinUpdate = {
        name: twinName,
        public_summary: publicSummary,
        short_url: username || undefined,
        title,
      }
      const result = await twinsUpdate(activeOrganization, myTwin.id, update)

      // update twins in recoil
      setTwins((twins) => {
        if (!twins) {
          return []
        }
        return twins.map((t) => {
          if (t.id === myTwin.id) {
            return result.data
          }
          return t
        })
      })

      // we also need to update the user record indicating that they finished updating twin profile
      const response = await usersUpdateAuthenticated({
        onboarding_steps_completed: [...user.onboarding_steps_completed, OnboardingStep.twin_profile_update_finished],
      })
      setUser(response.data)
      setToast({ title: 'Successfully created twin profile', severity: 'success' })

      // if we are missing insights or email responder permissions, we should redirect to the connect integrations stage
      if (missingPermissions) {
        setStage(OnboardingStages.connect_integrations)
        navigate('/onboarding?stage=connect_integrations', { replace: true })

        // [Analytics] Track user completion of twin profile creation, proceed to connect integrations
        trackButtonClick('[Onboarding 4] Finish twin profile creation - missing permissions')
      } else {
        setStage(OnboardingStages.subscribe)
        navigate('/onboarding?stage=subscribe', { replace: true })

        // [Analytics] Track user completion of twin profile creation, proceed to subscribe
        trackButtonClick('[Onboarding 4] Finish twin profile creation - no missing permissions')
      }
    } catch (error) {
      setToast({ title: 'Twin profile creation failed', severity: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const [isTwinOverviewModalOpen, setIsTwinOverviewModalOpen] = useState<boolean>(false)
  const onTwinOverviewClick = () => {
    setIsTwinOverviewModalOpen(true)
  }

  return (
    <StageContainerStack
      isMobile={isMobile}
      isIntroStage={false}
      sx={{
        mb: isMobile ? 10 : 0,
        alignItems: 'center',
        height: '100%',
        maxHeight: '600px',
      }}>
      <Stack width="100%">
        <Typography color="secondary" variant="h3" mb={1}>
          Create your twin's profile
        </Typography>

        <Stack direction="row" alignItems="center" justifyContent="center">
          <Typography
            color="secondary"
            variant="pS"
            mb={isMobile ? 1 : 3}
            sx={{
              color: COLORS.GRAY_2,
              display: 'flex',
            }}>
            Add some details to let other users easily find and interact with your twin.
            <Stack
              sx={{
                display: 'inline-block',
              }}>
              {!isMobile && (
                <Tooltip
                  enterTouchDelay={0}
                  title={
                    <Stack spacing={1}>
                      <Typography
                        variant="pXS"
                        sx={{
                          color: COLORS.PURE_WHITE,
                        }}>
                        A twin is a digital representation of your knowledge and is comprised of advanced deep learning
                        models.
                      </Typography>
                      <Typography
                        variant="pXS"
                        sx={{
                          color: COLORS.PURE_WHITE,
                        }}>
                        After connecting your twin to your data sources, it can assist you by auto-generating insights
                        and responses.
                      </Typography>
                      <Typography
                        variant="pXS"
                        sx={{
                          color: COLORS.PURE_WHITE,
                        }}>
                        Other users can also chat directly with your twin to get responses in real-time.
                      </Typography>
                    </Stack>
                  }>
                  <InfoOutlined
                    sx={{
                      color: COLORS.GRAY_3,
                      height: '18px',
                      width: '16px',
                      ml: '4px',
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
          </Typography>
        </Stack>

        {isMobile && (
          <Button
            variant="text"
            onClick={() => {
              onTwinOverviewClick()
            }}
            sx={{
              width: 'fit-content',
              mt: '14px',
              mb: '36px',
            }}>
            <Typography
              sx={{
                color: COLORS.JELLED_PRIMARY_BLUE,
              }}>
              What is a digital twin?
            </Typography>
          </Button>
        )}

        <Stack direction={isMobile ? 'column' : 'row-reverse'} spacing={isMobile ? 0 : 4}>
          <EditablePicture
            onPicturePicked={onPicturePicked}
            onPictureRemoved={onPictureRemoved}
            src={myTwin?.picture}
            changeLabel="picture"
            alt="Twin profile"
            style={{ borderRadius: '9999px', height: '160px', width: '160px', marginBottom: '12px' }}
          />

          <Stack width="100%">
            <TextField
              className="has-label"
              required={true}
              label="Name"
              placeholder="What is the name of your twin?"
              value={twinName}
              onChange={onNameChange}
              error={submitCount > 0 && isFieldEmpty(twinName)}
              helperText={submitCount > 0 && isFieldEmpty(twinName) ? 'Please enter a valid twin name' : ''}
              sx={{
                width: '100%',
                marginTop: '32px',
                marginBottom: '48px',
              }}
            />

            <TextField
              className="has-label"
              required={true}
              label="Headline"
              placeholder="e.g. Product Manager at Startup"
              value={title}
              onChange={onTitleChange}
              error={submitCount > 0 && isFieldEmpty(title)}
              helperText={submitCount > 0 && isFieldEmpty(title) ? 'Please enter a headline' : ''}
              sx={{
                width: '100%',
                marginBottom: '48px',
              }}
            />

            <UsernameInput
              twinId={myTwin?.id ?? ''}
              username={username}
              onShortUrlChange={onShortUrlChange}
              isUsernameAvailable={isUsernameAvailable}
              setIsUsernameAvailable={setIsUsernameAvailable}
              didSubmitForm={submitCount > 0}
              shouldCheckAvailabilityOnMount={true}
            />

            <TextField
              className="has-label"
              required={true}
              multiline
              rows={3}
              label="About"
              placeholder="What should users know about your twin? What are your areas of expertise? What is your background?"
              value={publicSummary}
              onChange={onPublicSummaryChange}
              error={submitCount > 0 && isFieldEmpty(publicSummary)}
              helperText={submitCount > 0 && isFieldEmpty(publicSummary) ? 'Please enter a public summary' : ''}
              sx={{
                mt: 5,
                width: '100%',
                marginBottom: isMobile ? '96px' : '16px',
              }}
            />
          </Stack>
        </Stack>
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        justifyContent="flex-end"
        width="100%"
        mr={0}
        position={isMobile ? 'fixed' : 'relative'}
        bottom={isMobile ? '24px' : 'auto'}
        right={isMobile ? '24px' : 'auto'}>
        <LoadingButton
          loading={isSubmitting}
          size="small"
          variant="contained"
          sx={{
            width: '84px',
          }}
          onClick={onNextClick}>
          Next
        </LoadingButton>
      </Stack>
      {isTwinOverviewModalOpen && <TwinOverviewModal onClose={() => setIsTwinOverviewModalOpen(false)} />}
    </StageContainerStack>
  )
}

const subscriptionFeatures = [
  'Your own model customized to you',
  'Full insights dashboard integrating Gmail and Slack',
  'Automatic email drafts in your voice using AI',
  'Unlimited use of your digital twin in chat and email',
]

const SubscribeStage = ({ isApplyingTopicsAsLabels }: { isApplyingTopicsAsLabels: boolean }) => {
  const [activeOrganization] = useActiveOrganization()
  const isMobile = useCheckMobileScreen()
  const myTwin = useRecoilValue(myTwinAtom)
  const user = useRecoilValue(authenticatedUserAtom)

  // if the user gets past the connections screen, we want to do the following:
  // - update the twin responder status to active
  // - depending on if they want to apply insight as gmail labels, we will set use_insight_topics_as_labels to true
  useEffect(() => {
    async function fetchData() {
      if (!activeOrganization || !myTwin) {
        return
      }
      try {
        await usersUpdateTwinResponder(activeOrganization, myTwin.id, {
          status: TwinResponderStatus.active,
          use_insight_topics_as_labels: isApplyingTopicsAsLabels ?? undefined,
        })
      } catch (error) {
        console.log('Failed to update twin responder status')
      }
    }

    // we only want to update twin responder status to active if the user had specified email_management as one of the objectives
    if (user?.objectives.includes(UserObjective.email_management)) {
      fetchData()
    }
  }, [activeOrganization, myTwin, isApplyingTopicsAsLabels, user])

  return (
    <StageContainerStack
      isMobile={isMobile}
      isIntroStage={false}
      sx={{
        mb: isMobile ? 10 : 0,
        alignItems: 'center',
      }}>
      <Stack width="100%">
        <Typography color="secondary" variant="h3" mb={1}>
          Start your 30-day free trial
        </Typography>

        <Typography
          color="secondary"
          variant="pS"
          mb={isMobile ? 1 : 3}
          sx={{
            color: COLORS.GRAY_2,
            textAlign: 'center',
            width: '100%',
          }}>
          Try free for 30 days, then $30/month. Cancel anytime.
        </Typography>

        <Stack
          maxWidth="494px"
          p="40px 38px 40px 40px"
          borderRadius="20px"
          sx={{
            alignSelf: 'center',
            border: `1px solid ${COLORS.GRAY_5}`,
          }}>
          <Stack direction="row" alignItems="center" spacing={1} justifyContent="center" mb={3}>
            <Typography
              sx={{
                fontSize: '42px',
                lineHeight: '20px',
                fontWeight: 500,
              }}>
              $30
            </Typography>

            <Typography
              variant="h5.5"
              sx={{
                color: COLORS.GRAY_3,
              }}>
              per month
            </Typography>
          </Stack>

          <Stack spacing={2} mb={7}>
            {subscriptionFeatures.map((feature, index) => (
              <Stack key={index} direction="row" alignItems="center" spacing={1}>
                <CheckCircleIcon
                  sx={{
                    color: COLORS.JELLED_PRIMARY_BLUE,
                    height: '24px',
                    width: '24px',
                  }}
                />
                <Typography
                  variant="pS"
                  sx={{
                    color: COLORS.GRAY_0,
                  }}>
                  {feature}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <Button
            size="medium"
            variant="contained"
            sx={{
              width: 'fit-content',
              alignSelf: 'center',
              mb: 2,
            }}
            onClick={() => {
              // [Analytics] Track user starting a 30-day trial
              trackButtonClick('[Onboarding 5] Start a 30-day trial')

              window.open(STRIPE.PAYMENT_URL, '_self')
            }}>
            Start a 30-Day Trial
          </Button>

          <Typography
            variant="pXS"
            sx={{
              color: COLORS.GRAY_2,
            }}>
            Don’t worry, we’ll send you a reminder before your trial expires.
          </Typography>
        </Stack>
      </Stack>
    </StageContainerStack>
  )
}

const Onboarding = () => {
  const [user] = useRecoilState(authenticatedUserAtom)
  const [activeOrganization] = useActiveOrganization()
  const isMobile = useCheckMobileScreen()
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()
  const onboardingStage = searchParams.get('stage') as OnboardingStages
  const [stage, setStage] = useState<OnboardingStages>(onboardingStage || OnboardingStages.intro)
  useEffect(() => {
    if (onboardingStage && onboardingStage !== stage) {
      setStage(onboardingStage as OnboardingStages)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const hasUpdatedTwinProfile = !!user?.onboarding_steps_completed.includes(OnboardingStep.twin_profile_update_finished)

  const insightsPermissions = useRecoilValue(featurePermissionsAtomFamily(Feature.insights))
  const acceptedPermissions = insightsPermissions?.acceptedPermissions ?? []
  const missingPermissions = insightsPermissions?.missingPermissions ?? []

  const emailResponderPermissions = useRecoilValue(featurePermissionsAtomFamily(Feature.email_responder))
  const emailResponderMissingPermissions = emailResponderPermissions?.missingPermissions ?? []

  const advancedEmailResponderPermissions = useRecoilValue(
    featurePermissionsAtomFamily(Feature.advanced_email_responder)
  )
  const advancedEmailResponderMissingPermissions = advancedEmailResponderPermissions?.missingPermissions ?? []

  useEffect(() => {
    if (onboardingStage && onboardingStage !== stage) {
      setStage(onboardingStage as OnboardingStages)
    }
  }, [onboardingStage, stage])

  useEffect(() => {
    const subscription = user?.profiles.find((p) => p.organization.id === activeOrganization)?.organization.subscription
    const isEternalOrEnterpriseOrBeta =
      subscription?.product_name === 'eternal' ||
      subscription?.product_name === 'enterprise' ||
      subscription?.product_name === 'beta'

    // if the user lands on onboarding, but they have a subcription or are part of eternal / enterprise subscription in their org
    // redirect them to insights page
    if ((isEternalOrEnterpriseOrBeta || !!user?.subscription) && hasUpdatedTwinProfile) {
      navigate('/')
      return
    }
  }, [user?.subscription, navigate, activeOrganization, user?.profiles, onboardingStage, hasUpdatedTwinProfile])

  // create account stage state
  const [tosChecked, setTosChecked] = useState<boolean>(false)

  const [title, setTitle] = useState<string>(user?.title || '')

  // twin profile stage state
  const [picture, setPicture] = useState<string>('')
  const [twinName, setTwinName] = useState<string>(`${user?.name}'s twin`)
  const [publicSummary, setTwinPublicSummary] = useState<string>('')
  const [username, setUsername] = useState<string>('')
  const onShortUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value)
  }
  const [isUsernameAvailable, setIsUsernameAvailable] = useState<boolean | null>(null)

  // topics of interest stage
  const [isApplyingTopicsAsLabels, setIsApplyingTopicsAsLabels] = useState<boolean>(true)

  // if we land here but the user has already completed onboarding, we should redirect them to the home page
  useEffect(() => {
    if (user?.onboarding_steps_completed.includes(OnboardingStep.twin_profile_update_finished) && !!user.subscription) {
      navigate('/')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // used only in the IntroStage component since we have 3 pages now
  const [page, setPage] = useState<number>(0)

  const renderStage = () => {
    // if the user has specified on the topics of interest stage that they'd like to apply topics as labels, then on the Link your Gmail and Google drive stage
    // we should pass in the advanced email responder permissions as well
    const allMissingPermissions = isApplyingTopicsAsLabels
      ? [...missingPermissions, ...emailResponderMissingPermissions, ...advancedEmailResponderMissingPermissions]
      : [...missingPermissions, ...emailResponderMissingPermissions]

    switch (stage) {
      case OnboardingStages.intro:
        return (
          <IntroStage
            tosChecked={tosChecked}
            setTosChecked={setTosChecked}
            page={page}
            setPage={setPage}
            setStage={setStage}
          />
        )
      case OnboardingStages.user_role:
        return <UserRoleStage setStage={setStage} />
      case OnboardingStages.function:
        return <FunctionStage setStage={setStage} />
      case OnboardingStages.main_objective:
        return <MainObjectiveStage setStage={setStage} />
      case OnboardingStages.topics_of_interest:
        return (
          <TopicsOfInterestStage
            isApplyingTopicsAsLabels={isApplyingTopicsAsLabels}
            setIsApplyingTopicsAsLabels={setIsApplyingTopicsAsLabels}
            setStage={setStage}
          />
        )
      case OnboardingStages.create_twin_profile:
        return (
          <CreateTwinProfileStage
            picture={picture}
            setPicture={setPicture}
            twinName={twinName}
            setTwinName={setTwinName}
            publicSummary={publicSummary}
            setTwinPublicSummary={setTwinPublicSummary}
            username={username}
            onShortUrlChange={onShortUrlChange}
            isUsernameAvailable={isUsernameAvailable}
            setIsUsernameAvailable={setIsUsernameAvailable}
            title={title}
            setStage={setStage}
            setTitle={setTitle}
            missingPermissions={allMissingPermissions.length > 0}
          />
        )
      case OnboardingStages.connect_integrations:
        return (
          <ConnectIntegrationsStage
            acceptedPermissions={acceptedPermissions}
            missingPermissions={allMissingPermissions}
          />
        )
      case OnboardingStages.subscribe:
        return <SubscribeStage isApplyingTopicsAsLabels={isApplyingTopicsAsLabels} />
    }
  }

  const renderGraphic = () => {
    if (stage === OnboardingStages.intro) {
      switch (page) {
        case 0:
          return (
            <IntroGraphic
              style={{
                width: '100%',
                height: '100%',
              }}
            />
          )
        case 1:
          return (
            <InsightsGraphic
              style={{
                width: '100%',
                height: '100%',
              }}
            />
          )
        case 2:
          return (
            <EmailsGraphic
              style={{
                width: '100%',
                height: '100%',
              }}
            />
          )
        case 3:
          return (
            <JelledbotsGraphic
              style={{
                width: '100%',
                height: '100%',
              }}
            />
          )
      }
    }

    switch (stage) {
      case OnboardingStages.user_role:
      case OnboardingStages.subscribe:
        return (
          <Stack mt={4} p={1} direction="row" alignItems={'flex-start'} justifyContent="center" flex="1">
            <AppIconLight
              style={{
                height: '19px',
                width: '19px',
              }}
            />
            <JelledTextLight
              style={{
                marginLeft: '10px',
                width: '75px',
                height: '20px',
              }}
            />
          </Stack>
        )
      case OnboardingStages.create_twin_profile:
        return (
          <Stack>
            <Stack mt={4} p={1} direction="row" alignItems={'flex-start'} justifyContent="center" flex="1">
              <AppIconLight
                style={{
                  height: '19px',
                  width: '19px',
                }}
              />
              <JelledTextLight
                style={{
                  marginLeft: '10px',
                  width: '75px',
                  height: '20px',
                }}
              />
            </Stack>

            <Stack px={4} height="calc(100vh - 160px)" justifyContent="center">
              <Typography
                variant="h4"
                mb={1}
                sx={{
                  color: COLORS.PURE_WHITE,
                }}>
                What is a digital twin?
              </Typography>

              <Typography
                mb={2}
                sx={{
                  color: COLORS.PURE_WHITE,
                }}>
                A twin is a digital representation of your knowledge and is comprised of advanced deep learning models.
              </Typography>

              <Typography
                mb={2}
                sx={{
                  color: COLORS.PURE_WHITE,
                }}>
                After connecting your twin to your data sources, it can assist you by auto-generating insights and
                responses.
              </Typography>

              <Typography
                mb={2}
                sx={{
                  color: COLORS.PURE_WHITE,
                }}>
                Other users can also chat directly with your twin to get responses in real-time.
              </Typography>
            </Stack>
          </Stack>
        )
      default:
        return null
    }
  }

  // we do not display any graphics in mobile view
  if (isMobile) {
    return <Stack>{renderStage()}</Stack>
  }

  const subscription = user?.profiles.find((p) => p.organization.id === activeOrganization)?.organization.subscription
  const isEternalOrEnterpriseOrBeta =
    subscription?.product_name === 'eternal' ||
    subscription?.product_name === 'enterprise' ||
    subscription?.product_name === 'beta'
  if (
    (isEternalOrEnterpriseOrBeta || user?.subscription) &&
    user?.onboarding_steps_completed.includes(OnboardingStep.twin_profile_update_finished)
  ) {
    return null
  }

  const isIntroStage = stage === OnboardingStages.intro

  if (isIntroStage) {
    return (
      <Stack width="100%" height="100%" position="absolute" top="0" left="0" right="0" bottom="0">
        <Stack direction="row" height="100vh">
          {renderStage()}
          {renderGraphic()}
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack
      width="100%"
      height="100%"
      justifyContent="center"
      alignItems="center"
      sx={{
        background: COLORS.GRAY_8,
      }}>
      {renderStage()}
    </Stack>
  )
}

export default Onboarding
